import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, SignOut, User, Lock, Phone, Target, Lightning, Scales, House, Notebook, ChartLine, UsersThree, WhatsappLogo, CaretDown, CaretUp, Trophy, TrendUp, TrendDown, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";

// Goal History Component
const GoalHistorySection = () => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await axios.get(`${API}/users/goal-history`);
        setHistory(response.data);
      } catch (error) {
        console.error('Error fetching goal history:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-4 shadow-card animate-pulse">
        <div className="h-6 bg-muted rounded w-1/2 mb-4"></div>
        <div className="h-20 bg-muted rounded"></div>
      </div>
    );
  }

  if (!history || history.total_profiles === 0) {
    return null; // Don't show if no history
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-card overflow-hidden"
      data-testid="goal-history-section"
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 bg-muted/30 flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <Trophy size={20} className="text-primary" weight="duotone" />
          <span className="font-bold text-foreground">Histórico de Objetivos</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{history.total_profiles} {history.total_profiles === 1 ? 'objetivo' : 'objetivos'}</span>
          {expanded ? <CaretUp size={20} /> : <CaretDown size={20} />}
        </div>
      </button>
      
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-4 space-y-4">
              {history.history.map((item, index) => {
                const isCurrentGoal = item.goal === history.current_goal && item.intensity === history.current_intensity;
                
                return (
                  <div
                    key={`${item.goal}-${item.intensity}-${index}`}
                    className={`rounded-xl p-4 border ${
                      isCurrentGoal 
                        ? 'border-primary bg-primary/5' 
                        : 'border-muted bg-muted/20'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{item.goal_emoji}</span>
                        <div>
                          <p className="font-bold text-foreground">{item.goal_label}</p>
                          <p className="text-xs text-muted-foreground">{item.intensity_label}</p>
                        </div>
                      </div>
                      {isCurrentGoal && (
                        <span className="text-xs bg-primary text-white px-2 py-1 rounded-full flex items-center gap-1">
                          <Check size={12} weight="bold" /> Atual
                        </span>
                      )}
                    </div>
                    
                    {/* Progress bar */}
                    <div className="mb-2">
                      <div className="flex justify-between text-xs text-muted-foreground mb-1">
                        <span>{item.start_weight}kg</span>
                        <span>{item.progress_text}</span>
                        <span>{item.target_weight}kg</span>
                      </div>
                      <Progress value={item.progress_percent} className="h-2" />
                    </div>
                    
                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <div className="text-center bg-white/50 rounded-lg p-2">
                        <p className="text-lg font-bold font-mono text-foreground">{item.daily_calories}</p>
                        <p className="text-xs text-muted-foreground">kcal/dia</p>
                      </div>
                      <div className="text-center bg-white/50 rounded-lg p-2">
                        <p className="text-lg font-bold font-mono flex items-center justify-center gap-1 text-foreground">
                          {item.goal === 'ganhar' ? (
                            <TrendUp size={16} className="text-green-500" />
                          ) : item.goal === 'perder' ? (
                            <TrendDown size={16} className="text-blue-500" />
                          ) : (
                            <Scales size={16} className="text-primary" />
                          )}
                          {Math.round(item.progress_percent)}%
                        </p>
                        <p className="text-xs text-muted-foreground">progresso</p>
                      </div>
                    </div>
                    
                    {/* Date */}
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                      Iniciado em {new Date(item.created_at).toLocaleDateString('pt-PT', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const Profile = () => {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogWeight, setShowLogWeight] = useState(false);
  const [newWeight, setNewWeight] = useState("");

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Profile error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogWeight = async () => {
    if (!newWeight) {
      toast.error("Por favor, insere o teu peso");
      return;
    }

    try {
      await axios.post(`${API}/weight`, { weight: parseFloat(newWeight) });
      toast.success("Peso registrado!");
      setShowLogWeight(false);
      setNewWeight("");
      fetchProfile();
    } catch (error) {
      toast.error("Erro ao registrar peso");
    }
  };

  const handleChangeGoal = () => {
    // Clear goal data and navigate to focus selection
    updateUser({ goal: null, daily_calories: null });
    navigate('/focus');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success("Até breve!");
  };

  const handleSupport = () => {
    const phone = "244957040934";
    const message = encodeURIComponent("Olá, preciso de suporte com a minha conta Nzadi Fit.");
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <div className="text-primary font-bold text-xl">Nzadi Fit</div>
          <div className="text-muted-foreground text-sm mt-1">Carregando...</div>
        </div>
      </div>
    );
  }

  const goalLabels = {
    perder: "🔥 Perder Peso",
    manter: "⚖️ Manter Peso",
    ganhar: "💪 Ganhar Peso"
  };

  const activityLabels = {
    sedentario: "Sedentário",
    ativo: "Ativo(a)",
    intenso: "Intenso(a)"
  };

  const statusLabels = {
    trial: "Período de Teste",
    ativo: "Ativo",
    expired: "Expirado",
    pending: "Pendente"
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-primary text-white p-4 flex items-center gap-4">
        <button 
          onClick={() => navigate('/')}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft size={24} weight="bold" />
        </button>
        <h1 className="text-xl font-bold">Meu Perfil</h1>
      </header>

      <main className="p-6 space-y-6">
        {/* Avatar & Name */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center text-center"
        >
          <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <User size={48} className="text-primary" weight="duotone" />
          </div>
          <h2 className="text-2xl font-bold text-foreground">{profile?.name}</h2>
          <p className="text-muted-foreground flex items-center gap-2">
            <Phone size={16} />
            {profile?.phone}
          </p>
          
          {/* Subscription Status */}
          <div className={`mt-3 px-4 py-2 rounded-full text-sm font-medium ${
            profile?.subscription_status === 'ativo' 
              ? 'bg-primary/10 text-primary' 
              : profile?.subscription_status === 'trial'
                ? 'bg-zest/10 text-zest'
                : 'bg-destructive/10 text-destructive'
          }`}>
            {statusLabels[profile?.subscription_status] || profile?.subscription_status}
          </div>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-4 shadow-card text-center"
          >
            <Scales size={24} className="text-primary mx-auto mb-2" weight="duotone" />
            <p className="text-2xl font-bold font-mono">{profile?.current_weight || '--'}</p>
            <p className="text-xs text-muted-foreground">Peso Atual (kg)</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-4 shadow-card text-center"
          >
            <Target size={24} className="text-zest mx-auto mb-2" weight="duotone" />
            <p className="text-2xl font-bold font-mono">{profile?.daily_calories || '--'}</p>
            <p className="text-xs text-muted-foreground">Meta Diária (kcal)</p>
          </motion.div>
        </div>

        {/* Log Weight Button */}
        <Button
          onClick={() => setShowLogWeight(true)}
          className="w-full bg-primary/10 text-primary hover:bg-primary/20 rounded-2xl py-6"
          data-testid="log-weight-button"
        >
          <Scales size={24} className="mr-2" />
          Registrar Peso Atual
        </Button>

        {/* Profile Info */}
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          <h3 className="font-bold text-foreground p-4 bg-muted/30">Informações do Perfil</h3>
          <div className="divide-y divide-muted">
            {[
              { icon: Target, label: "Objetivo", value: goalLabels[profile?.goal] || profile?.goal || '--' },
              { icon: Lightning, label: "Nível de Atividade", value: activityLabels[profile?.intensity] || profile?.intensity || '--' },
              { icon: Scales, label: "Peso Meta", value: profile?.target_weight ? `${profile.target_weight} kg` : '--' }
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <Icon size={20} className="text-primary" weight="duotone" />
                  <span className="text-foreground">{label}</span>
                </div>
                <span className="text-muted-foreground font-medium">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Change Goal Button */}
        <Button
          onClick={handleChangeGoal}
          variant="outline"
          className="w-full rounded-2xl border-primary text-primary hover:bg-primary/10 py-6"
          data-testid="change-goal-button"
        >
          <Target size={24} className="mr-2" />
          Alterar Objetivo Atual
        </Button>

        {/* Goal History Section */}
        <GoalHistorySection />

        {/* Referral Code */}
        {profile?.referral_code && (
          <div className="bg-primary/10 rounded-2xl p-4">
            <p className="text-sm text-muted-foreground mb-1">O teu código de afiliado:</p>
            <p className="text-xl font-bold text-primary font-mono">{profile.referral_code}</p>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {profile?.subscription_status !== 'ativo' && (
            <Button
              onClick={() => navigate('/payment')}
              className="btn-primary w-full"
              data-testid="upgrade-button"
            >
              <Lock size={20} className="mr-2" />
              Fazer Upgrade
            </Button>
          )}

          {/* Support Button */}
          <Button
            onClick={handleSupport}
            className="w-full rounded-2xl bg-green-500 hover:bg-green-600 text-white py-6"
            data-testid="support-button"
          >
            <WhatsappLogo size={24} weight="fill" className="mr-2" />
            Suporte Nzadi Fit
          </Button>

          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full rounded-2xl border-destructive text-destructive hover:bg-destructive/10"
            data-testid="logout-button"
          >
            <SignOut size={20} className="mr-2" />
            Sair da Conta
          </Button>
        </div>
      </main>

      {/* Log Weight Dialog */}
      <Dialog open={showLogWeight} onOpenChange={setShowLogWeight}>
        <DialogContent className="bg-white rounded-3xl border-none max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle>Registrar Peso</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <input
              type="number"
              step="0.1"
              placeholder="Ex: 68.5"
              value={newWeight}
              onChange={(e) => setNewWeight(e.target.value)}
              className="input-nzadi text-center text-2xl font-mono"
              data-testid="new-weight-input"
            />
            <p className="text-center text-muted-foreground mt-2">kg</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLogWeight(false)} className="rounded-full">
              Cancelar
            </Button>
            <Button onClick={handleLogWeight} className="btn-primary">
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-muted p-2 flex justify-around items-center z-50 safe-bottom">
        {[
          { icon: House, label: "Início", path: "/" },
          { icon: Notebook, label: "Diário", path: "/diary" },
          { icon: ChartLine, label: "Progresso", path: "/progress" },
          { icon: UsersThree, label: "Afiliados", path: "/affiliates" },
          { icon: User, label: "Perfil", path: "/profile", active: true }
        ].map(({ icon: Icon, label, path, active }) => (
          <button
            key={path}
            onClick={() => navigate(path)}
            className={`flex flex-col items-center p-2 rounded-xl transition-colors ${
              active ? "text-primary" : "text-muted-foreground hover:text-primary"
            }`}
          >
            <Icon size={24} weight={active ? "fill" : "regular"} />
            <span className="text-xs mt-1">{label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
};

export default Profile;
